import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import {
  ArrowBack,
  Add as AddIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "shared/hooks/auth";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import {
  TipoMaterialN1,
  TIPO_MATERIAL_LABELS,
  IProdutoListagemN1,
  IFiltrosListagemN1,
  IOrdenacaoListagemN1,
  IPaginacaoListagemN1,
  ITipoMaterialPermitido,
  ITiposPermitidosResponse,
  IHistoricoImportacao,
} from "./types/materiaisN1.types";
import {
  listarProdutosN1,
  obterTiposPermitidos,
  baixarModeloPlanilha,
  importarProdutos,
  exportarProdutos,
  homologarProdutosEmLote,
  concluirHomologacaoEmLote,
  liberarCemEmLote,
  ITipoSelecionadoN1,
  obterAndamentoGeracaoPacotes,
  obterContagemSituacoes,
  IContagemSituacoes,
  obterHistoricoImportacoes,
  removerUltimaImportacao,
  verificarImpactosN1,
  IImpactoN1,
} from "./services/materiaisN1";
import { BarraControles } from "./components/BarraControles";
import { BarraAcoesStatus } from "./components/BarraAcoesStatus";
import { GridMateriais } from "./components/GridMateriais";
import { ModalNovoCadastro } from "./components/modals/ModalNovoCadastro";
import { ModalImportacao } from "./components/modals/ModalImportacao";
import { ModalExportar } from "./components/modals/ModalExportar";
import { ModalConfirmacaoHomologar } from "./components/modals/ModalConfirmacaoHomologar";
import { ModalConfirmacaoConcluirHomologacao } from "./components/modals/ModalConfirmacaoConcluirHomologacao";
import { ModalLiberarCem } from "./components/modals/ModalLiberarCem";
import { useTravaPlanilhaN1 } from "./hooks/useTravaPlanilhaN1";

const STORAGE_KEY_TIPO_MATERIAL = "@MOF:produtosN1:tipoMaterial";
const STORAGE_KEY_MODO_EDICAO = "@MOF:produtosN1:modoEdicao";

// FASE 05 — 2ª validação/PO 2026-07-24 (6.2): rótulo do status para a lista de códigos
// nas confirmações (Ativo/Descontinuado).
const rotuloStatusProduto = (status?: string): string => {
  if (status === "ativo") return "Ativo";
  if (status === "descontinuado") return "Descontinuado";
  return status || "";
};

/**
 * FASE 05 — Ajustes 25/08 (item 1 / decisão F05A-P1 de 26/08): Acabamentos ganhou página
 * própria. Em vez de duplicar esta tela inteira, ela é parametrizada:
 *
 *  - `apenasAcabamentos` = false (padrão) → página PRODUTOS, com os 5 tipos no seletor
 *    (Acabamentos saiu, conforme P1);
 *  - `apenasAcabamentos` = true → página ACABAMENTOS, sem seletor de tipo, fixa em
 *    acabamentos.
 *
 * O recorte vale para tudo o que a tela faz: listagem, contagem de elegíveis, universo dos
 * botões de fluxo e — por consequência, no servidor — qual pacote do CEM é regravado.
 */
interface ProdutosN1Props {
  apenasAcabamentos?: boolean;
}

export const ProdutosN1: React.FC<ProdutosN1Props> = ({ apenasAcabamentos = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openNotification } = useNavigation();

  // Estados principais
  const [tipoMaterialSelecionado, setTipoMaterialSelecionado] = useState<TipoMaterialN1 | null>(null);
  const [modoEdicaoAtivo, setModoEdicaoAtivo] = useState<boolean>(false);
  const [produtos, setProdutos] = useState<IProdutoListagemN1[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tiposPermitidos, setTiposPermitidos] = useState<ITipoMaterialPermitido[]>([]);
  const [permissoes, setPermissoes] = useState<ITiposPermitidosResponse["permissoes"]>({
    podeCadastrar: false,
    podeImportar: false,
    podeExportar: true,
  });
  // FASE 05 — 2ª validação/PO 2026-07-24 (B2): empresa "apenas para consulta".
  const [apenasConsulta, setApenasConsulta] = useState<boolean>(false);

  // Estados dos modais
  const [modalNovoCadastroOpen, setModalNovoCadastroOpen] = useState(false);
  const [modalImportacaoOpen, setModalImportacaoOpen] = useState(false);
  const [modalExportarOpen, setModalExportarOpen] = useState(false);
  const [modalHomologarOpen, setModalHomologarOpen] = useState(false);
  const [modalConcluirHomologacaoOpen, setModalConcluirHomologacaoOpen] = useState(false);
  // FASE 05 — 2ª validação/PO 2026-07-24 (6.1): "Disponibilizar Mercado" (substitui Liberar CEM Delphi/Mobi).
  const [modalDisponibilizarOpen, setModalDisponibilizarOpen] = useState(false);
  // Produtos da empresa na situação-alvo da ação (carregados ao abrir o modal — a ação afeta todos).
  // FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2/item #1): a ação vale para TODOS os tipos;
  // cada item carrega seu tipoMaterial para a execução chamar o endpoint certo por tipo.
  const [produtosAcao, setProdutosAcao] = useState<Array<IProdutoListagemN1 & { tipoMaterial: TipoMaterialN1 }>>([]);
  // Etapa D (itens 3 e 4): impacto do acionamento, exibido nos modais de confirmacao.
  const [impactoAcao, setImpactoAcao] = useState<IImpactoN1 | null>(null);
  // FASE 05 — 6ª validação/PO 2026-08-03 (A4-i): incrementado após cada ação de fluxo
  // (Homologar/Concluir/Disponibilizar) para forçar o recarregamento da listagem pelo
  // caminho canônico (useEffect de carga, com indicador de loading do grid) — antes cada
  // handler recarregava "no silêncio" e o grid não refletia a nova situação sem F5.
  const [reloadKey, setReloadKey] = useState(0);
  const [historicoImportacoes, setHistoricoImportacoes] = useState<IHistoricoImportacao[]>([]);
  const [ultimaImportacao, setUltimaImportacao] = useState<IHistoricoImportacao | null>(null);
  const [isLoadingAcoes, setIsLoadingAcoes] = useState(false);

  // Plano 14/10/2026 (Frente 4): exclusão mútua planilha × edição manual do tipo selecionado.
  // Releitura a cada ação de fluxo (reloadKey) e a cada importação/remoção (ultimaImportacao).
  const { trava: travaPlanilha, recarregar: recarregarTravaPlanilha } = useTravaPlanilhaN1(tipoMaterialSelecionado);
  useEffect(() => {
    recarregarTravaPlanilha();
  }, [reloadKey, ultimaImportacao, recarregarTravaPlanilha]);
  const planilhaPendente = !!travaPlanilha?.planilhaPendente;
  const edicaoManualPendente = !!travaPlanilha?.edicaoManualPendente;

  // Estados de filtros, ordenação e paginação
  // FASE 05 — 2ª validação/PO 2026-07-24 (B3-default): a primeira visão da tela mostra
  // apenas produtos com status Ativo; o usuário pode marcar Descontinuado no filtro.
  const [filtros, setFiltros] = useState<IFiltrosListagemN1>({ status: ["ativo"] });
  const [ordenacao, setOrdenacao] = useState<IOrdenacaoListagemN1>({
    campo: null,
    direcao: null,
  });
  const [paginacao, setPaginacao] = useState<IPaginacaoListagemN1>({
    page: 0,
    limit: 100, // Default conforme especificação
    total: 0,
  });

  // Carregar tipos permitidos ao montar componente
  useEffect(() => {
    const carregarTiposPermitidos = async () => {
      // Log temporário para debug
      console.log("[ProdutosN1] user:", user);
      console.log("[ProdutosN1] user?.idEmpresa:", user?.idEmpresa);
      console.log("[ProdutosN1] sessionStorage user:", sessionStorage.getItem("@MOF:user"));
      console.log("[ProdutosN1] sessionStorage token:", sessionStorage.getItem("@MOF:token") ? "existe" : "não existe");
      
      const idEmpresa = user?.idEmpresa;
      
      if (!idEmpresa) {
        console.warn("[ProdutosN1] IdEmpresa não encontrado. User:", user);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        console.log("[ProdutosN1] Chamando obterTiposPermitidos com idEmpresa:", idEmpresa);
        const response = await obterTiposPermitidos(idEmpresa);
        if (response) {
          // F05A-P1: cada página enxerga só o seu universo de tipos — a de Acabamentos
          // fica com acabamentos, a de Produtos com os outros cinco.
          const tiposDaPagina = response.tipos.filter(t =>
            apenasAcabamentos
              ? t.tipoMaterial === "acabamentos"
              : t.tipoMaterial !== "acabamentos"
          );

          setTiposPermitidos(tiposDaPagina);
          setPermissoes(response.permissoes);
          setApenasConsulta(response.apenasConsulta);
          // Em modo consulta não há edição: garante o toggle desligado mesmo que
          // o sessionStorage tenha um valor antigo "true".
          if (response.apenasConsulta) {
            setModoEdicaoAtivo(false);
            sessionStorage.setItem(STORAGE_KEY_MODO_EDICAO, "false");
          }

          // Na página de Acabamentos não há escolha: o tipo é fixo.
          if (apenasAcabamentos) {
            setTipoMaterialSelecionado(tiposDaPagina[0]?.tipoMaterial ?? null);
          } else {
            // Carregar último tipo selecionado ou usar o primeiro disponível
            const ultimoTipo = sessionStorage.getItem(STORAGE_KEY_TIPO_MATERIAL) as TipoMaterialN1 | null;
            const tipoInicial = ultimoTipo && tiposDaPagina.some(t => t.tipoMaterial === ultimoTipo)
              ? ultimoTipo
              : (tiposDaPagina[0]?.tipoMaterial || null);

            setTipoMaterialSelecionado(tipoInicial);
          }
        } else {
          // Se não retornou dados, limpar estados
          setTiposPermitidos([]);
        }
      } catch (error) {
        console.error("Erro ao carregar tipos permitidos:", error);
        setTiposPermitidos([]);
      } finally {
        setIsLoading(false);
      }
    };

    carregarTiposPermitidos();
  }, [user, apenasAcabamentos]);

  // Carregar modo edição do sessionStorage
  useEffect(() => {
    const modoEdicaoSalvo = sessionStorage.getItem(STORAGE_KEY_MODO_EDICAO);
    if (modoEdicaoSalvo !== null) {
      setModoEdicaoAtivo(modoEdicaoSalvo === "true");
    }
  }, []);

  // Carregar produtos quando tipo material, filtros, ordenação ou paginação mudarem
  useEffect(() => {
    if (!tipoMaterialSelecionado || !user?.idEmpresa) return;

    const carregarProdutos = async () => {
      setIsLoading(true);
      const response = await listarProdutosN1(
        tipoMaterialSelecionado,
        paginacao,
        filtros,
        ordenacao,
        modoEdicaoAtivo
      );

      if (response) {
        setProdutos(response.items);
        setPaginacao(prev => ({ ...prev, total: response.total }));
      } else {
        setProdutos([]);
        setPaginacao(prev => ({ ...prev, total: 0 }));
      }

      setIsLoading(false);
    };

    carregarProdutos();
  }, [tipoMaterialSelecionado, paginacao.page, paginacao.limit, filtros, ordenacao, modoEdicaoAtivo, user?.idEmpresa, reloadKey]);

  // Handler: Mudança de tipo material
  const handleTipoMaterialChange = useCallback((novoTipo: TipoMaterialN1) => {
    setTipoMaterialSelecionado(novoTipo);
    sessionStorage.setItem(STORAGE_KEY_TIPO_MATERIAL, novoTipo);
    // FASE 05 — 6ª validação/PO 2026-08-03 (A1): ao trocar o tipo, preservar o default de
    // Status "Ativo" (mesmo de :104). Antes zerava tudo (setFiltros({})) e o grid passava a
    // mostrar também Descontinuados.
    setFiltros({ status: ["ativo"] });
    setPaginacao(prev => ({ ...prev, page: 0 }));
  }, []);

  // Handler: Mudança de modo edição
  const handleModoEdicaoChange = useCallback((ativo: boolean) => {
    setModoEdicaoAtivo(ativo);
    sessionStorage.setItem(STORAGE_KEY_MODO_EDICAO, ativo.toString());
  }, []);

  // Handler: Mudança de filtros
  const handleFiltrosChange = useCallback((novosFiltros: IFiltrosListagemN1) => {
    setFiltros(novosFiltros);
    setPaginacao(prev => ({ ...prev, page: 0 }));
  }, []);

  // Handler: Mudança de ordenação
  const handleOrdenacaoChange = useCallback((novaOrdenacao: IOrdenacaoListagemN1) => {
    setOrdenacao(novaOrdenacao);
  }, []);

  // Handler: Mudança de página
  const handlePageChange = useCallback((page: number) => {
    setPaginacao(prev => ({ ...prev, page }));
  }, []);

  // Handler: Mudança de limite por página
  const handlePageSizeChange = useCallback((limit: number) => {
    setPaginacao(prev => ({ ...prev, limit, page: 0 }));
  }, []);

  // Carregar histórico de importações quando modal de importação abrir
  useEffect(() => {
    if (!modalImportacaoOpen || !tipoMaterialSelecionado || !user?.idEmpresa) return;

    const carregarHistorico = async () => {
      const historico = await obterHistoricoImportacoes(user.idEmpresa, tipoMaterialSelecionado);
      if (historico && historico.length > 0) {
        setHistoricoImportacoes(historico);
        // Última importação é a mais recente
        const ultima = historico.sort((a, b) => 
          new Date(b.dataHoraImportacao).getTime() - new Date(a.dataHoraImportacao).getTime()
        )[0];
        setUltimaImportacao(ultima);
      } else {
        setHistoricoImportacoes([]);
        setUltimaImportacao(null);
      }
    };

    carregarHistorico();
  }, [modalImportacaoOpen, tipoMaterialSelecionado, user?.idEmpresa]);

  // Handlers dos modais
  const handleImportar = useCallback(async (tipoMaterial: TipoMaterialN1, arquivo: File) => {
    if (!user?.idEmpresa) return;
    
    setIsLoadingAcoes(true);
    try {
      const resultado = await importarProdutos({
        tipoMaterial,
        idEmpresa: user.idEmpresa,
        arquivo,
      });

      if (resultado?.sucesso) {
        openNotification({
          message: `Importação concluída: ${resultado.linhasAceitas} linhas aceitas, ${resultado.linhasRecusadas} linhas recusadas.`,
          type: "success"
        });
        // Recarregar produtos
        const response = await listarProdutosN1(
          tipoMaterialSelecionado!,
          paginacao,
          filtros,
          ordenacao,
          modoEdicaoAtivo
        );
        if (response) {
          setProdutos(response.items);
          setPaginacao(prev => ({ ...prev, total: response.total }));
        }
        // Recarregar histórico de importações
        const historico = await obterHistoricoImportacoes(user.idEmpresa, tipoMaterialSelecionado!);
        if (historico && historico.length > 0) {
          setHistoricoImportacoes(historico);
          const ultima = historico.sort((a, b) => 
            new Date(b.dataHoraImportacao).getTime() - new Date(a.dataHoraImportacao).getTime()
          )[0];
          setUltimaImportacao(ultima);
        }
        // Plano 15/10/2026 (Frente 5): a contagem por situação (visibilidade de HOMOLOGAR etc.)
        // só relê pelo reloadKey — sem isto o botão ficava defasado até o F5.
        setReloadKey((k) => k + 1);
      } else {
        openNotification({ message: "Erro ao importar produtos. Verifique o arquivo e tente novamente.", type: "error" });
      }
    } catch (error: any) {
      // FASE 05 — 5ª validação/P1 2026-07-30: a recusa da importação traz o motivo linha a
      // linha (referência não liberada, aba obrigatória ausente etc.) — mostrar a mensagem
      // real em vez de engolir no console.
      console.error("Erro na importação:", error);
      openNotification({
        message: error?.message || "Erro ao importar produtos. Verifique o arquivo e tente novamente.",
        type: "error",
      });
    } finally {
      setIsLoadingAcoes(false);
    }
  }, [user?.idEmpresa, tipoMaterialSelecionado, paginacao, filtros, ordenacao, modoEdicaoAtivo, openNotification]);

  const handleExportar = useCallback(async () => {
    if (!tipoMaterialSelecionado || !user?.idEmpresa) return;

    setIsLoadingAcoes(true);
    try {
      await exportarProdutos({
        tipoMaterial: tipoMaterialSelecionado,
        filtros,
        modoEdicao: modoEdicaoAtivo,
      });
      openNotification({ message: "Exportação concluída com sucesso", type: "success" });
      setModalExportarOpen(false);
    } catch (error) {
      console.error("Erro na exportação:", error);
    } finally {
      setIsLoadingAcoes(false);
    }
  }, [tipoMaterialSelecionado, user?.idEmpresa, filtros, modoEdicaoAtivo, openNotification]);

  // Breadcrumb
  const breadcrumbItems = useMemo(() => {
    if (!tipoMaterialSelecionado) return [];
    return [
      { label: "Home", path: "/dashboard" },
      { label: "Cadastros", path: "#" },
      { label: TIPO_MATERIAL_LABELS[tipoMaterialSelecionado], path: "#" },
    ];
  }, [tipoMaterialSelecionado]);

  // FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2/item #1): a ação vale para TODOS os tipos
  // de produto da empresa na situação-alvo (Perfis+Componentes+Vidros+Superfícies+Kits+
  // Acabamentos), não só o tipo do seletor. Busca por tipo, marca cada item com o tipoMaterial
  // e abre o modal com a lista completa (todos os tipos).
  const abrirAcaoNaSituacao = useCallback(async (situacaoAlvo: string, abrirModal: () => void) => {
    if (tiposPermitidos.length === 0) return;
    setIsLoadingAcoes(true);
    try {
      const agregados: Array<IProdutoListagemN1 & { tipoMaterial: TipoMaterialN1 }> = [];
      for (const t of tiposPermitidos) {
        const resp = await listarProdutosN1(
          t.tipoMaterial,
          { page: 0, limit: 1000, total: 0 },
          { situacao: [situacaoAlvo] },
          undefined,
          true
        );
        const itens = (resp?.items ?? []).filter(p => p.origem === 'Empresa');
        agregados.push(...itens.map(p => ({ ...p, tipoMaterial: t.tipoMaterial })));
      }
      setProdutosAcao(agregados);

      // FASE 05 Ajustes 31/08 — Etapa D (itens 3 e 4): consulta o impacto ANTES de abrir o modal,
      // para os avisos já aparecerem na confirmação. Vale nos três botões (F05B-P4) e apenas
      // informa — nenhum dos avisos bloqueia a transição (F05B-P8).
      const porTipo = Object.entries(
        agregados.reduce((acc, p) => {
          (acc[p.tipoMaterial] ||= []).push(p.id);
          return acc;
        }, {} as Record<string, number[]>)
      ).map(([tipoMaterial, idsProdutos]) => ({ tipoMaterial, idsProdutos } as ITipoSelecionadoN1));

      // Best-effort: falha na consulta de impacto não pode impedir a abertura do modal.
      try {
        setImpactoAcao(
          porTipo.length > 0 && user?.idEmpresa
            ? await verificarImpactosN1(user.idEmpresa, porTipo)
            : null
        );
      } catch {
        setImpactoAcao(null);
      }

      abrirModal();
    } finally {
      setIsLoadingAcoes(false);
    }
  }, [tiposPermitidos, user?.idEmpresa]);

  // FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2): executa a ação por tipo — agrupa
  // produtosAcao por tipoMaterial e chama o endpoint (por tipo). Cada tipo é um SaveChanges
  // independente: se um falhar (ex.: 500 intermitente do Disponibilizar), os demais seguem e a
  // falha é reportada (falha parcial), sem reverter o que já gravou.
  // FASE 05 — Ajustes 25/08 (item 7): o acionamento vira UMA chamada com todos os tipos.
  // Antes era um POST por tipo, e cada POST mandava um e-mail e disparava uma geração
  // completa de pacotes — 4 tipos selecionados rendiam 4 e-mails e 4 gerações.
  const itensSelecionadosPorTipo = useCallback((): ITipoSelecionadoN1[] => {
    const porTipo = new Map<TipoMaterialN1, number[]>();
    for (const p of produtosAcao) {
      const ids = porTipo.get(p.tipoMaterial) ?? [];
      ids.push(p.id);
      porTipo.set(p.tipoMaterial, ids);
    }
    return Array.from(porTipo, ([tipoMaterial, idsProdutos]) => ({ tipoMaterial, idsProdutos }));
  }, [produtosAcao]);

  const executarEmLote = useCallback(async (
    acao: (idEmpresa: number, itens: ITipoSelecionadoN1[]) => Promise<{ sucesso: boolean; total: number } | null>
  ): Promise<{ total: number; tiposFalha: TipoMaterialN1[] }> => {
    if (!user?.idEmpresa) return { total: 0, tiposFalha: [] };

    const itens = itensSelecionadosPorTipo();
    if (itens.length === 0) return { total: 0, tiposFalha: [] };

    try {
      const r = await acao(user.idEmpresa, itens);
      // A chamada agora é atômica do ponto de vista da tela: ou o acionamento processou,
      // ou nenhum tipo processou (o servidor segue tolerando falha parcial por tipo).
      return r?.sucesso
        ? { total: r.total, tiposFalha: [] }
        : { total: r?.total ?? 0, tiposFalha: itens.map(i => i.tipoMaterial) };
    } catch {
      return { total: 0, tiposFalha: itens.map(i => i.tipoMaterial) };
    }
  }, [user?.idEmpresa, itensSelecionadosPorTipo]);

  // FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2): notificação agregada (todos os tipos),
  // cobrindo sucesso total, falha parcial, falha total e "nada elegível".
  const notificarResultadoAcao = useCallback((
    total: number,
    tiposFalha: TipoMaterialN1[],
    situacaoOrigem: string,
    verboSucesso: string
  ) => {
    const labelsFalha = tiposFalha.map(t => TIPO_MATERIAL_LABELS[t]).join(", ");
    if (tiposFalha.length > 0 && total > 0) {
      openNotification({ message: `${total} produto(s) ${verboSucesso}. Falha em: ${labelsFalha} — tente novamente para esses tipos.`, type: "warning" });
    } else if (tiposFalha.length > 0) {
      openNotification({ message: `Não foi possível processar. Falha em: ${labelsFalha}. Tente novamente.`, type: "error" });
    } else if (total > 0) {
      openNotification({ message: `${total} produto(s) ${verboSucesso}.`, type: "success" });
    } else {
      openNotification({ message: `Nenhum produto na situação ${situacaoOrigem} foi processado. Atualize a página e tente novamente.`, type: "warning" });
    }
  }, [openNotification]);

  // FASE 05 Ajustes — PO 2026-08-24 (item 6 / P1, substitui a P1 de 04/08): os botões de fluxo
  // só aparecem com produtos na situação habilitante; a contagem multi-tipo vem do servidor e é
  // recarregada ao ligar o Modo edição e após cada ação de fluxo (reloadKey).
  const [contagemSituacoes, setContagemSituacoes] = useState<IContagemSituacoes | null>(null);

  useEffect(() => {
    if (!modoEdicaoAtivo || !user?.idEmpresa) {
      setContagemSituacoes(null);
      return;
    }
    let ativo = true;
    // F05A-P1: conta só os tipos desta página — senão um perfil elegível faria o botão
    // aparecer na página de Acabamentos (e vice-versa).
    obterContagemSituacoes(tiposPermitidos.map(t => t.tipoMaterial)).then((contagem) => {
      if (ativo) setContagemSituacoes(contagem);
    });
    return () => { ativo = false; };
  }, [modoEdicaoAtivo, user?.idEmpresa, reloadKey, tiposPermitidos]);

  // Plano 17/08/2026 (Frente 5 / decisao P9): os botoes Homologar e Disponibilizar
  // Mercado passaram a gerar os pacotes de N1 do CEM Desktop. A geracao roda em segundo
  // plano no servidor, entao a tela precisa mostrar que ela esta em andamento — inclusive
  // depois de um F5, por isso o estado e consultado no servidor, e nao guardado aqui.
  const [gerandoPacotes, setGerandoPacotes] = useState(false);

  const verificarAndamentoPacotes = useCallback(async () => {
    const andamento = await obterAndamentoGeracaoPacotes();
    const emAndamento = andamento?.emAndamento === true;
    setGerandoPacotes(emAndamento);
    return emAndamento;
  }, []);

  // Consulta ao abrir a tela: se uma geracao ficou rodando, o usuario ve isso de cara.
  useEffect(() => {
    verificarAndamentoPacotes();
  }, [verificarAndamentoPacotes]);

  // Enquanto estiver gerando, reconsulta a cada 5s e avisa ao terminar.
  useEffect(() => {
    if (!gerandoPacotes) return;

    const intervalo = setInterval(async () => {
      const aindaGerando = await verificarAndamentoPacotes();
      if (!aindaGerando) {
        openNotification({
          message: "Geração dos pacotes para o CEM Desktop concluída.",
          type: "success",
        });
      }
    }, 5000);

    return () => clearInterval(intervalo);
  }, [gerandoPacotes, verificarAndamentoPacotes, openNotification]);

  const handleHomologar = useCallback(async () => {
    if (!user?.idEmpresa) return;
    const idEmpresa = user.idEmpresa;
    setIsLoadingAcoes(true);
    try {
      const { total, tiposFalha } = await executarEmLote(homologarProdutosEmLote);
      notificarResultadoAcao(total, tiposFalha, "Em Edição", "homologados com sucesso");
      setModalHomologarOpen(false);
      // A geracao dos pacotes ja partiu no servidor: liga o indicador de andamento.
      await verificarAndamentoPacotes();
      // Recarregar pelo caminho canônico — reflete o estado real com loading do grid (A4-i).
      setReloadKey(k => k + 1);
    } catch (error) {
      console.error("Erro ao homologar:", error);
      openNotification({ message: "Erro ao homologar os produtos. Tente novamente.", type: "error" });
    } finally {
      setIsLoadingAcoes(false);
    }
  }, [executarEmLote, notificarResultadoAcao, openNotification, verificarAndamentoPacotes]);

  const handleConcluirHomologacao = useCallback(async () => {
    if (!user?.idEmpresa) return;
    const idEmpresa = user.idEmpresa;
    setIsLoadingAcoes(true);
    try {
      const { total, tiposFalha } = await executarEmLote(concluirHomologacaoEmLote);
      notificarResultadoAcao(total, tiposFalha, "Em Homologação", "com homologação concluída");
      setModalConcluirHomologacaoOpen(false);
      // Recarregar pelo caminho canônico (A4-i).
      setReloadKey(k => k + 1);
    } catch (error) {
      console.error("Erro ao concluir homologação:", error);
      openNotification({ message: "Erro ao concluir a homologação. Tente novamente.", type: "error" });
    } finally {
      setIsLoadingAcoes(false);
    }
  }, [executarEmLote, notificarResultadoAcao, openNotification]);

  // Verificar se usuário é Master
  const isMaster = useMemo(() => {
    return user?.role?.includes("Master") || false;
  }, [user?.role]);

  if (!tipoMaterialSelecionado || tiposPermitidos.length === 0) {
    return (
      <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
        <BackdropLoading isOpen={isLoading} />
        <StackCustom>
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: "center" }}>
            {isLoading ? "Carregando tipos de materiais permitidos..." : "Nenhum tipo de material permitido para esta empresa."}
          </Typography>
        </StackCustom>
      </DashboardStackCustom>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      {/* FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2): a ação por todos os tipos faz
          várias buscas antes de abrir o modal; o backdrop bloqueia clique duplo e dá feedback. */}
      <BackdropLoading isOpen={isLoadingAcoes} />
      <Grid container display={"flex"} alignItems={"center"}>
        <StackCustom sx={{ 
          backgroundColor: "#ECEDED !important",
          boxShadow: "none !important",
          border: "none !important",
        }}>
          {/* Header: Título + Botões de Ação */}
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="flex-start" 
            mb={0}
          >
            {/* Lado esquerdo: Seta voltar + Título */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                sx={{ color: "#666" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
                {apenasAcabamentos ? "ACABAMENTOS" : "PRODUTOS"}
              </Typography>
            </Stack>

            {/* Lado direito: Botões de Ação */}
            <Stack direction="row" spacing={1}>
              {/* FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2/item #1): os botões de fluxo
                  (Homologar/Concluir/Disponibilizar) foram realocados para cá (topo-direito);
                  Cadastrar/Importar/Exportar desceram para a linha do "Listagem de". */}
              <BarraAcoesStatus
                modoEdicaoAtivo={modoEdicaoAtivo}
                isMaster={isMaster}
                gerandoPacotes={gerandoPacotes}
                contagemSituacoes={contagemSituacoes}
                onHomologar={() => abrirAcaoNaSituacao('em_edicao', () => setModalHomologarOpen(true))}
                onConcluirHomologacao={() => abrirAcaoNaSituacao('em_homologacao', () => setModalConcluirHomologacaoOpen(true))}
                onDisponibilizarMercado={() => abrirAcaoNaSituacao('homologado', () => setModalDisponibilizarOpen(true))}
              />
            </Stack>
          </Stack>

          {/* Breadcrumb abaixo do título */}
          <Box sx={{ 
            borderBottom: "2px solid #3C90AD", 
            pb: 1.5, 
            mb: 2,
            ml: 4.5,
          }}>
            <Breadcrumbs separator="›" sx={{ mt: 0 }}>
              {breadcrumbItems.map((item, index) => (
                <Link
                  key={index}
                  color={index === breadcrumbItems.length - 1 ? "text.primary" : "inherit"}
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    if (index === 0) navigate("/dashboard");
                  }}
                  sx={{ textDecoration: "none", cursor: "pointer", fontSize: "0.7rem" }}
                >
                  {item.label}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {/* Barra de Controles (Dropdown + Toggle) com Botões de Ação */}
          <Box sx={{ mb: 2, px: 4.5 }}>
            <BarraControles
              ocultarSeletorTipo={apenasAcabamentos}
              tiposPermitidos={tiposPermitidos}
              tipoMaterialSelecionado={tipoMaterialSelecionado}
              modoEdicaoAtivo={modoEdicaoAtivo}
              apenasConsulta={apenasConsulta}
              onTipoMaterialChange={handleTipoMaterialChange}
              onModoEdicaoChange={handleModoEdicaoChange}
            >
              {/* FASE 05 — 6ª validação/PO 2026-08-04 (Frente 2/item #1): Cadastrar/Importar/
                  Exportar realocados para a linha do "Listagem de" (o fluxo subiu para o topo). */}
              {/* CADASTRAR e IMPORTAR ficam ocultos em empresa "apenas consulta" (B2). */}
              {!apenasConsulta && (
              <>
              {/* Botão CADASTRAR */}
              <Tooltip
                title={
                  !permissoes.podeCadastrar || !modoEdicaoAtivo
                    ? !modoEdicaoAtivo
                      ? "Ative o modo edição para cadastrar"
                      : "Sem permissão para cadastrar produtos"
                    : planilhaPendente
                      ? travaPlanilha?.mensagemPlanilhaPendente ?? ""
                      : "Cadastrar novo produto"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    endIcon={<AddIcon />}
                    onClick={() => setModalNovoCadastroOpen(true)}
                    // Plano 14/10/2026 (Frente 4): planilha do tipo em andamento bloqueia cadastro manual.
                    disabled={!permissoes.podeCadastrar || !modoEdicaoAtivo || planilhaPendente}
                    sx={{
                      backgroundColor: "#3C90AD",
                      "&:hover": { backgroundColor: "#2d6f87" },
                      textTransform: "uppercase",
                      fontWeight: 600,
                      borderRadius: 0,
                      boxShadow: "none",
                      px: 3,
                      py: 1.5,
                      fontSize: "0.875rem",
                      "&:disabled": {
                        backgroundColor: "#b0c4cc",
                        color: "#fff",
                      },
                    }}
                  >
                    CADASTRAR
                  </Button>
                </span>
              </Tooltip>

              {/* Botão IMPORTAR */}
              <Tooltip
                title={
                  !permissoes.podeImportar || !modoEdicaoAtivo
                    ? !modoEdicaoAtivo
                      ? "Ative o modo edição para importar"
                      : "Sem permissão para importar produtos"
                    : edicaoManualPendente
                      // Plano 14/10/2026 (Frente 4): o botão segue habilitado porque o modal também
                      // importa mídias e mostra o histórico; a importação da planilha é que fica
                      // bloqueada lá dentro (e no back).
                      ? travaPlanilha?.mensagemEdicaoManualPendente ?? ""
                      : "Importar produtos de planilha"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    endIcon={<UploadIcon />}
                    onClick={() => setModalImportacaoOpen(true)}
                    disabled={!permissoes.podeImportar || !modoEdicaoAtivo}
                    sx={{
                      backgroundColor: "#3C90AD",
                      "&:hover": { backgroundColor: "#2d6f87" },
                      textTransform: "uppercase",
                      fontWeight: 600,
                      borderRadius: 0,
                      boxShadow: "none",
                      px: 3,
                      py: 1.5,
                      fontSize: "0.875rem",
                      "&:disabled": {
                        backgroundColor: "#b0c4cc",
                        color: "#fff",
                      },
                    }}
                  >
                    IMPORTAR
                  </Button>
                </span>
              </Tooltip>
              </>
              )}

              {/* Botão EXPORTAR */}
              <Tooltip title="Exportar produtos para planilha">
                <Button
                  variant="contained"
                  endIcon={<DownloadIcon />}
                  onClick={() => setModalExportarOpen(true)}
                  sx={{
                    backgroundColor: "#3C90AD",
                    "&:hover": { backgroundColor: "#2d6f87" },
                    textTransform: "uppercase",
                    fontWeight: 600,
                    borderRadius: 0,
                    boxShadow: "none",
                    px: 3,
                    py: 1.5,
                    fontSize: "0.875rem",
                  }}
                >
                  EXPORTAR
                </Button>
              </Tooltip>
            </BarraControles>
          </Box>

          {/* Grid de Materiais */}
          <GridMateriais
            produtos={produtos}
            isLoading={isLoading}
            filtros={filtros}
            ordenacao={ordenacao}
            paginacao={paginacao}
            onFiltrosChange={handleFiltrosChange}
            onOrdenacaoChange={handleOrdenacaoChange}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onVisualizarDetalhes={(id) => {
              if (!tipoMaterialSelecionado) return;
              const map: Record<string, string> = {
                perfis: "/produtosN1/perfis",
                componentes: "/produtosN1/componentes",
                vidros: "/produtosN1/vidros",
                superficies: "/produtosN1/superficies",
                kits: "/produtosN1/kits",
                acabamentos: "/produtosN1/acabamentos",
              };
              const base = map[tipoMaterialSelecionado];
              if (!base) return;
              navigate(`${base}/${id}`);
            }}
          />
        </StackCustom>
      </Grid>

      {/* Modais */}
      <ModalNovoCadastro
        open={modalNovoCadastroOpen}
        onClose={() => setModalNovoCadastroOpen(false)}
        tiposPermitidos={tiposPermitidos}
      />

      <ModalImportacao
        open={modalImportacaoOpen}
        onClose={() => setModalImportacaoOpen(false)}
        tiposPermitidos={tiposPermitidos}
        tipoMaterialSelecionado={tipoMaterialSelecionado}
        onImportar={handleImportar}
        onBaixarModelo={async (tipoMaterial: TipoMaterialN1) => {
          setIsLoadingAcoes(true);
          try {
            await baixarModeloPlanilha(tipoMaterial);
          } finally {
            setIsLoadingAcoes(false);
          }
        }}
        onRemoverUltimaImportacao={ultimaImportacao ? async () => {
          if (!tipoMaterialSelecionado || !user?.idEmpresa) return;
          setIsLoadingAcoes(true);
          try {
            const sucesso = await removerUltimaImportacao(ultimaImportacao.idImportacao);
            if (sucesso) {
              openNotification({ message: "Última importação removida com sucesso.", type: "success" });
              // Recarregar produtos e histórico
              const response = await listarProdutosN1(
                tipoMaterialSelecionado,
                paginacao,
                filtros,
                ordenacao,
                modoEdicaoAtivo
              );
              if (response) {
                setProdutos(response.items);
                setPaginacao(prev => ({ ...prev, total: response.total }));
              }
              const historico = await obterHistoricoImportacoes(user.idEmpresa, tipoMaterialSelecionado);
              if (historico && historico.length > 0) {
                setHistoricoImportacoes(historico);
                const ultima = historico.sort((a, b) => 
                  new Date(b.dataHoraImportacao).getTime() - new Date(a.dataHoraImportacao).getTime()
                )[0];
                setUltimaImportacao(ultima);
              } else {
                setHistoricoImportacoes([]);
                setUltimaImportacao(null);
              }
              // Plano 15/10/2026 (Frente 5): relê a contagem dos botões de fluxo (HOMOLOGAR ficava
              // visível após devolver os produtos à situação anterior).
              setReloadKey((k) => k + 1);
            } else {
              // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 12): falha "soft" (retorno false
              // sem exceção) deixou de ser silenciosa.
              openNotification({ message: "Não foi possível remover a última importação. Tente novamente.", type: "error" });
            }
          } catch (error) {
            console.error("Erro ao remover importação:", error);
            openNotification({ message: "Erro ao remover a última importação. Tente novamente.", type: "error" });
          } finally {
            setIsLoadingAcoes(false);
          }
        } : undefined}
        historicoImportacoes={historicoImportacoes}
        ultimaImportacao={ultimaImportacao}
        isLoading={isLoadingAcoes}
      />

      <ModalExportar
        open={modalExportarOpen}
        onClose={() => setModalExportarOpen(false)}
        tipoMaterial={tipoMaterialSelecionado!}
        totalFiltrados={paginacao.total}
        onExportar={handleExportar}
      />

      <ModalConfirmacaoHomologar
        open={modalHomologarOpen}
        onClose={() => setModalHomologarOpen(false)}
        onConfirmar={handleHomologar}
        quantidade={produtosAcao.length}
        codigosProdutos={produtosAcao.map(p => `${TIPO_MATERIAL_LABELS[p.tipoMaterial]}: ${p.codigo} — ${rotuloStatusProduto(p.status)}`)}
        impacto={impactoAcao}
        isLoading={isLoadingAcoes}
      />

      <ModalConfirmacaoConcluirHomologacao
        open={modalConcluirHomologacaoOpen}
        onClose={() => setModalConcluirHomologacaoOpen(false)}
        onConfirmar={handleConcluirHomologacao}
        quantidade={produtosAcao.length}
        codigosProdutos={produtosAcao.map(p => `${TIPO_MATERIAL_LABELS[p.tipoMaterial]}: ${p.codigo} — ${rotuloStatusProduto(p.status)}`)}
        impacto={impactoAcao}
        isLoading={isLoadingAcoes}
      />

      <ModalLiberarCem
        open={modalDisponibilizarOpen}
        onClose={() => setModalDisponibilizarOpen(false)}
        titulo="Disponibilizar Mercado"
        totalSelecionados={produtosAcao.length}
        codigosProdutos={produtosAcao.map(p => `${TIPO_MATERIAL_LABELS[p.tipoMaterial]}: ${p.codigo} — ${rotuloStatusProduto(p.status)}`)}
        impacto={impactoAcao}
        loading={isLoadingAcoes}
        onConfirm={async () => {
          setIsLoadingAcoes(true);
          try {
            const { total, tiposFalha } = await executarEmLote(
              (empresa, itens) => liberarCemEmLote(empresa, itens, "delphi")
            );
            notificarResultadoAcao(total, tiposFalha, "Homologado", "disponibilizados para o mercado com sucesso");
            setModalDisponibilizarOpen(false);
            await verificarAndamentoPacotes();
            // Recarregar pelo caminho canônico — reflete o estado real com loading do grid (A4-i).
            setReloadKey((k) => k + 1);
          } finally {
            setIsLoadingAcoes(false);
          }
        }}
      />
    </DashboardStackCustom>
  );
};
